import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { ChevronDown, ChevronUp, Play, Power, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AGENT_SHORT = {
  copywriter_agent: 'CW', planner_agent: 'PL', strategist_agent: 'ST',
  scout_agent: 'SC', sales_agent: 'SA', funnel_agent: 'FN',
  events_agent: 'EV', designer_agent: 'DS', publisher_agent: 'PB',
  operator_agent: 'OP', analyst_agent: 'AN', partnership_agent: 'PA',
  ads_monitor_agent: 'AD',
};

const AGENT_LABELS = {
  copywriter_agent: 'Copywriter', planner_agent: 'Planner', strategist_agent: 'Strategist',
  scout_agent: 'Scout', sales_agent: 'Sales', funnel_agent: 'Funnel',
  events_agent: 'Evento', designer_agent: 'Designer', publisher_agent: 'Publisher',
  operator_agent: 'Operator', analyst_agent: 'Analyst', partnership_agent: 'Partnership',
  ads_monitor_agent: 'Ads Monitor',
};

const FALLBACK_SLIDERS = [
  { id: 'brand-conversion', name: 'Brand - Conversion', leftLabel: 'Brand', rightLabel: 'Conversion', value: 35,
    description: 'Al 35%: il sistema favorisce contenuti educativi e brand.' },
  { id: 'organic-paid', name: 'Organico - Paid', leftLabel: 'Organico', rightLabel: 'Paid', value: 20,
    description: 'Al 20%: il sistema massimizza contenuto organico.' },
  { id: 'volume-depth', name: 'Volume - Profondita', leftLabel: 'Volume', rightLabel: 'Profondita', value: 60,
    description: 'Al 60%: il sistema bilancia frequenza e qualita.' },
  { id: 'education-promo', name: 'Educazione - Promozione', leftLabel: 'Educazione', rightLabel: 'Promozione', value: 30,
    description: 'Al 30%: la maggioranza dei contenuti e educativa.' },
];

function scoreColor(s) { return s >= 85 ? '#16A34A' : s >= 70 ? '#F59E0B' : '#DC2626'; }

function sliderDesc(id, val) {
  const descs = {
    'brand-conversion': val < 30 ? `Al ${val}%: focus quasi esclusivo su brand awareness.`
      : val < 50 ? `Al ${val}%: favorisce contenuti educativi e brand.`
      : val < 70 ? `Al ${val}%: equilibrio brand/conversion.`
      : `Al ${val}%: focus su conversion.`,
    'organic-paid': val < 30 ? `Al ${val}%: massimizza contenuto organico.`
      : val < 50 ? `Al ${val}%: principalmente organico.`
      : val < 70 ? `Al ${val}%: mix bilanciato organico/paid.`
      : `Al ${val}%: strategia paid-first.`,
    'volume-depth': val < 30 ? `Al ${val}%: alta frequenza, contenuti piu brevi.`
      : val < 50 ? `Al ${val}%: buona frequenza con qualita media.`
      : val < 70 ? `Al ${val}%: bilancia frequenza e qualita.`
      : `Al ${val}%: focus su profondita.`,
    'education-promo': val < 30 ? `Al ${val}%: la maggioranza dei contenuti e educativa.`
      : val < 50 ? `Al ${val}%: prevalenza educativa.`
      : val < 70 ? `Al ${val}%: mix equilibrato educativo/promo.`
      : `Al ${val}%: focus promozionale.`,
  };
  return descs[id] || '';
}

export default function AgentsPage() {
  const [activeTab, setActiveTab] = useState('team');
  const [agents, setAgents] = useState([]);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Orientation state
  const [sliders, setSliders] = useState(FALLBACK_SLIDERS);
  const [origValues, setOrigValues] = useState(FALLBACK_SLIDERS.map(s => s.value));
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [orchRes, orientRes, histRes, activityRes] = await Promise.allSettled([
        api.get('/agents/orchestra-status'),
        api.get('/strategy/orientation'),
        api.get('/strategy/orientation/history'),
        api.get('/dashboard/activity?limit=100'),
      ]);
      // Build activity map: agent_name → last activity description
      const activityMap = {};
      if (activityRes.status === 'fulfilled') {
        const activities = activityRes.value.data || [];
        activities.forEach(act => {
          const agentName = act.agent_name || act.agent;
          if (agentName && !activityMap[agentName]) {
            activityMap[agentName] = act.description || act.action_description || act.details || act.summary || act.title || act.action || '';
          }
        });
      }
      // Readable action fallback map
      const readableAction = {
        'bus_dispatch': 'Elaborazione messaggi',
        'content_recycling': 'Riciclo contenuti evento',
        'product_deleted': 'Gestione catalogo prodotti',
        'F3': 'Newsletter generata', 'F1': 'Post LinkedIn generato',
        'F2': 'Post aziendale generato', 'F6': 'Recap evento generato',
        'content_generated': 'Contenuto generato', 'content_published': 'Contenuto pubblicato',
        'newsletter_sent': 'Newsletter inviata', 'plan_generated': 'Piano generato',
      };

      if (orchRes.status === 'fulfilled') {
        const data = orchRes.value.data;
        const list = data.agents || [];
        setAgents(list.map(a => {
          // Build score from real data if available
          const itemsToday = a.items_today || 0;
          const totalItems = a.total_items || 0;
          const computedScore = a.score ?? a.health_score ?? (
            itemsToday > 5 ? 95 : itemsToday > 2 ? 88 : itemsToday > 0 ? 75 :
            totalItems > 20 ? 82 : totalItems > 5 ? 70 : a.is_active !== false ? 65 : 40
          );
          // Build last work from multiple sources
          const lastAction = a.last_action || '';
          let lastWork = a.last_output_summary || a.action_description || a.details || '';
          if (!lastWork && activityMap[a.agent_name]) lastWork = activityMap[a.agent_name];
          if (!lastWork && a.status_message && a.status_message !== 'idle') lastWork = a.status_message;
          if (!lastWork && readableAction[lastAction]) lastWork = readableAction[lastAction];
          if (!lastWork && lastAction && !lastAction.includes('_') && lastAction.length > 3) lastWork = lastAction;
          if (!lastWork) lastWork = a.is_active ? 'In attesa di task' : 'Disabilitato';

          return {
            id: a.agent_name,
            name: AGENT_LABELS[a.agent_name] || a.agent_name.replace('_agent', ''),
            initials: AGENT_SHORT[a.agent_name] || a.agent_name.slice(0, 2).toUpperCase(),
            active: a.is_active !== false,
            lastWork,
            score: computedScore,
            outputs: (a.recent_outputs || []).map(o => ({
              icon: (o.type || 'P')[0].toUpperCase(),
              title: o.title || o.summary || '',
              destination: o.destination || '',
              time: o.time || '',
            })),
            metrics: (a.metrics || []).map(m => ({ label: m.label, value: String(m.value) })),
          };
        }));
      }
      if (orientRes.status === 'fulfilled') {
        const d = orientRes.value.data;
        if (d.sliders?.length) {
          const mapped = d.sliders.map(s => ({
            id: s.id || s.key,
            name: s.name || s.label,
            leftLabel: s.left_label || s.leftLabel || '',
            rightLabel: s.right_label || s.rightLabel || '',
            value: s.value ?? 50,
            description: s.description || sliderDesc(s.id || s.key, s.value ?? 50),
          }));
          setSliders(mapped);
          setOrigValues(mapped.map(s => s.value));
        }
      }
      if (histRes.status === 'fulfilled') {
        setHistory(histRes.value.data?.history || histRes.value.data || []);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleAgent = async (agentName) => {
    try {
      const agent = agents.find(a => a.id === agentName);
      if (!agent) return;
      await api.patch(`/agents/${agentName}/config`, { is_active: !agent.active });
      toast.success(`${agent.name} ${agent.active ? 'disabilitato' : 'abilitato'}`);
      await loadData();
    } catch { toast.error('Errore toggle agente'); }
  };

  const forceRun = async (agentName) => {
    try {
      await api.post(`/agents/${agentName}/run`);
      toast.success('Esecuzione avviata');
    } catch { toast.error('Esecuzione forzata non disponibile'); }
  };

  const handleSliderChange = (id, val) => {
    setSliders(prev => prev.map(s => s.id === id ? { ...s, value: val, description: sliderDesc(id, val) } : s));
  };

  const saveOrientation = async () => {
    setSaving(true);
    try {
      await api.post('/strategy/orientation', { sliders: sliders.map(s => ({ key: s.id, value: s.value })) });
      toast.success('Orientamento salvato');
      setOrigValues(sliders.map(s => s.value));
    } catch { toast.error('Errore salvataggio'); }
    setSaving(false);
  };

  const hasChanges = sliders.some((s, i) => s.value !== origValues[i]);
  const activeCount = agents.filter(a => a.active).length;

  if (loading) return (
    <div className="flex h-full items-center justify-center" data-testid="agents-loading">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#2563EB' }} />
    </div>
  );

  return (
    <div className="h-full overflow-y-auto" data-testid="agents-page">
      {/* Tabs */}
      <div className="sticky top-0 z-10 bg-white" style={{ borderBottom: '1px solid #E5E7EB' }}>
        <div className="flex gap-0 px-6">
          {[{ id: 'team', label: 'Team Agenti' }, { id: 'orientamento', label: 'Orientamento strategico' }].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className="px-6 py-4 text-[14px] font-medium transition-colors"
              style={{ color: activeTab === t.id ? '#2563EB' : '#7C8CA2', borderBottom: `2px solid ${activeTab === t.id ? '#2563EB' : 'transparent'}`, marginBottom: '-1px' }}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Team Tab */}
        {activeTab === 'team' && (
          <>
            <div className="mb-6">
              <h1 className="mb-2 text-[18px] font-bold" style={{ color: '#1B2B4B' }}>Team Agenti</h1>
              <p className="text-[13px]" style={{ color: '#7C8CA2' }}>
                {activeCount} attivi su {agents.length}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {agents.map(agent => {
                const isExpanded = expandedAgent === agent.id;
                return (
                  <div key={agent.id} className="rounded-xl transition-all"
                    style={{ border: `1px solid ${isExpanded ? '#2563EB' : '#E5E7EB'}`, opacity: agent.active ? 1 : 0.6 }}
                    data-testid={`agent-card-${agent.id}`}>
                    <button onClick={() => setExpandedAgent(isExpanded ? null : agent.id)}
                      className="flex w-full items-center justify-between p-4 text-left">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full text-[13px] font-bold text-white"
                          style={{ backgroundColor: '#2563EB' }}>{agent.initials}</div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[14px] font-bold" style={{ color: '#1B2B4B' }}>{agent.name}</span>
                            <div className="flex items-center gap-1">
                              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: agent.active ? '#16A34A' : '#9CA3AF' }} />
                              <span className="text-[11px]" style={{ color: '#7C8CA2' }}>{agent.active ? 'Attivo' : 'Inattivo'}</span>
                            </div>
                          </div>
                          <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{agent.lastWork}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[20px] font-bold" style={{ color: scoreColor(agent.score) }}>{agent.score}</span>
                        {isExpanded ? <ChevronUp className="h-5 w-5" style={{ color: '#9CA3AF' }} /> : <ChevronDown className="h-5 w-5" style={{ color: '#9CA3AF' }} />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-3" style={{ borderTop: '1px solid #E5E7EB' }}>
                        {agent.outputs.length > 0 && (
                          <div className="mb-4">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>Output recenti</p>
                            <div className="space-y-2">
                              {agent.outputs.map((o, i) => (
                                <div key={i} className="flex w-full items-start gap-2 rounded-lg p-2 text-left hover:bg-gray-50">
                                  <span className="flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold" style={{ backgroundColor: '#DBEAFE', color: '#2563EB' }}>{o.icon}</span>
                                  <div className="flex-1">
                                    <p className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{o.title}</p>
                                    {o.destination && <p className="text-[10px]" style={{ color: '#2563EB' }}>→ {o.destination}</p>}
                                  </div>
                                  <span className="text-[11px]" style={{ color: '#9CA3AF' }}>{o.time}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {agent.metrics.length > 0 && (
                          <div className="mb-4">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>Metriche</p>
                            <div className="flex gap-4">
                              {agent.metrics.map((m, i) => (
                                <div key={i}>
                                  <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{m.label}</p>
                                  <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>{m.value}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div>
                          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>Azioni</p>
                          <div className="flex gap-2">
                            <button onClick={() => toggleAgent(agent.id)}
                              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-gray-50"
                              style={{ border: '1px solid #E5E7EB', color: '#6B7280' }}
                              data-testid={`toggle-${agent.id}`}>
                              <Power className="h-3.5 w-3.5" />{agent.active ? 'Disabilita' : 'Abilita'}
                            </button>
                            <button onClick={() => forceRun(agent.id)}
                              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-blue-50"
                              style={{ border: '1px solid #2563EB', color: '#2563EB' }}>
                              <Play className="h-3.5 w-3.5" />Forza esecuzione
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Orientation Tab */}
        {activeTab === 'orientamento' && (
          <div className="mx-auto max-w-[700px]">
            <div className="mb-8">
              <h1 className="mb-3 text-[18px] font-bold" style={{ color: '#1B2B4B' }}>Orientamento strategico</h1>
              <p className="text-[14px] leading-relaxed" style={{ color: '#7C8CA2' }}>
                Questi cursori guidano tutti gli agenti. Modificali per cambiare la direzione operativa dell'intero sistema.
              </p>
            </div>
            <div className="mb-8 space-y-6">
              {sliders.map(slider => (
                <div key={slider.id} className="rounded-xl p-5" style={{ border: '1px solid #E5E7EB' }} data-testid={`slider-${slider.id}`}>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-[15px] font-bold" style={{ color: '#1B2B4B' }}>{slider.name}</h3>
                    <span className="text-[16px] font-bold" style={{ color: '#2563EB' }}>{slider.value}%</span>
                  </div>
                  <div className="mb-3">
                    <input type="range" min="0" max="100" value={slider.value}
                      onChange={e => handleSliderChange(slider.id, parseInt(e.target.value))}
                      className="h-2 w-full cursor-pointer appearance-none rounded-full"
                      style={{ background: `linear-gradient(to right, #2563EB 0%, #2563EB ${slider.value}%, #E5E7EB ${slider.value}%, #E5E7EB 100%)` }} />
                    <div className="mt-1 flex justify-between">
                      <span className="text-[12px]" style={{ color: '#9CA3AF' }}>{slider.leftLabel}</span>
                      <span className="text-[12px]" style={{ color: '#9CA3AF' }}>{slider.rightLabel}</span>
                    </div>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#F9FAFB' }}>
                    <p className="text-[12px] leading-relaxed" style={{ color: '#374151' }}>{slider.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* History */}
            <div className="mb-6">
              <button onClick={() => setHistoryOpen(!historyOpen)}
                className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-gray-50"
                style={{ border: '1px solid #E5E7EB' }}
                data-testid="orientation-history-toggle">
                <span className="text-[13px] font-medium" style={{ color: '#6B7280' }}>Storico modifiche</span>
                {historyOpen ? <ChevronUp className="h-4 w-4" style={{ color: '#9CA3AF' }} /> : <ChevronDown className="h-4 w-4" style={{ color: '#9CA3AF' }} />}
              </button>
              {historyOpen && (
                <div className="mt-2 space-y-2 rounded-lg p-3" style={{ border: '1px solid #E5E7EB' }}>
                  {history.length === 0 ? (
                    <p className="text-[12px]" style={{ color: '#9CA3AF' }}>Nessuna modifica registrata</p>
                  ) : history.map((h, i) => (
                    <div key={i} className="text-[12px]" style={{ color: '#6B7280' }}>
                      <span className="font-medium">{h.date || h.changed_at}:</span> {h.slider || h.key} da {h.from}% a {h.to}% — <span className="italic">Motivo: {h.reason || '-'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-center">
              <button onClick={saveOrientation} disabled={!hasChanges || saving}
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-[14px] font-medium text-white transition-opacity"
                style={{ backgroundColor: '#2563EB', opacity: hasChanges ? 1 : 0.5, cursor: hasChanges ? 'pointer' : 'not-allowed' }}
                data-testid="save-orientation-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva modifiche
              </button>
              <p className="mt-2 text-[12px]" style={{ color: '#9CA3AF' }}>
                Le modifiche vengono applicate al prossimo checkpoint dell'orchestrator (ogni 6 ore).
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
